import React, { useState, useMemo, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Search, Wand2, ShieldCheck, ShieldAlert, Download, Undo2,
  ChevronLeft, ChevronRight, Loader2, X, Filter,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

/**
 * Admin → Text Repair.
 *
 * Scan → review → approve → apply flow for the corrupted-spacing repair.
 * Every mutation goes through the backend's audit-logged endpoints; this
 * page does NOT mutate any state on its own. Three tabs:
 *
 *   - Scan & Review: pull candidates, select rows, apply.
 *   - Batches: list of prior repair batches with one-click rollback.
 *
 * Wire constraints (matched to the brief):
 *   - No row is preselected for "apply". Operator must opt in per-row.
 *   - Low-confidence rows are blocked from apply unless the operator
 *     explicitly acknowledges them (modal confirmation).
 *   - Search filter + pagination are client-side over the scan result;
 *     server returns the full result set in one shot.
 */
const AdminTextRepair = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('scan');

  // ── scan state ────────────────────────────────────────────────────
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // { items, metrics, scanned, scan_id, confidence_safe_floor }
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [filterRule, setFilterRule] = useState('all');
  const [filterLowConf, setFilterLowConf] = useState(false);
  const [page, setPage] = useState(0);

  // ── apply state ───────────────────────────────────────────────────
  const [applying, setApplying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acknowledgeLowConf, setAcknowledgeLowConf] = useState(false);
  const [applyNote, setApplyNote] = useState('');

  // ── batches state ─────────────────────────────────────────────────
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null); // batch_id currently in flight

  // ── helpers ───────────────────────────────────────────────────────
  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${t}` };
  }, []);

  const keyOf = (it) => `${it.collection}|${it.doc_id}|${it.element}|${it.field}`;

  const fetchBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/text-repair/batches?limit=50`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBatches(data.batches || []);
    } catch (e) {
      toast({ title: 'Failed to load batches', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setBatchesLoading(false);
    }
  }, [authHeaders, toast]);

  useEffect(() => {
    if (activeTab === 'batches') fetchBatches();
  }, [activeTab, fetchBatches]);

  // ── derived state for the table ───────────────────────────────────
  const items = scanResult?.items || [];
  const safeFloor = scanResult?.confidence_safe_floor ?? 0.75;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filterRule !== 'all' && it.rule !== filterRule) return false;
      if (filterLowConf && it.confidence >= safeFloor) return false;
      if (!q) return true;
      return [it.original, it.proposed, it.doc_id, it.element, it.collection]
        .some((s) => String(s || '').toLowerCase().includes(q));
    });
  }, [items, search, filterRule, filterLowConf, safeFloor]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, pageCount - 1);
  const pageItems = filtered.slice(pageSafe * PAGE_SIZE, (pageSafe + 1) * PAGE_SIZE);

  // Reset to first page when filters change.
  useEffect(() => { setPage(0); }, [search, filterRule, filterLowConf]);

  const allOnPageSelected = pageItems.length > 0
    && pageItems.every((it) => selectedKeys.has(keyOf(it)));
  const someOnPageSelected = pageItems.some((it) => selectedKeys.has(keyOf(it)));

  const selectedItems = useMemo(
    () => items.filter((it) => selectedKeys.has(keyOf(it))),
    [items, selectedKeys],
  );
  const selectedLowConfCount = selectedItems.filter(
    (it) => it.confidence < safeFloor,
  ).length;

  // ── handlers ──────────────────────────────────────────────────────
  const runScan = async () => {
    setScanning(true);
    setSelectedKeys(new Set());
    try {
      const res = await fetch(`${API_URL}/api/admin/text-repair/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${await res.text()}`);
      const data = await res.json();
      setScanResult(data);
      toast({
        title: 'Scan complete',
        description: `${data.items?.length || 0} candidate(s) found across ${
          Object.values(data.scanned || {}).reduce((a, b) => a + b, 0)
        } document(s).`,
      });
    } catch (e) {
      toast({ title: 'Scan failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setScanning(false);
    }
  };

  const toggleRow = (it) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = keyOf(it);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const togglePage = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const allOn = pageItems.every((it) => next.has(keyOf(it)));
      pageItems.forEach((it) => allOn ? next.delete(keyOf(it)) : next.add(keyOf(it)));
      return next;
    });
  };

  const selectAllHighConf = () => {
    setSelectedKeys(new Set(
      filtered.filter((it) => it.confidence >= safeFloor).map(keyOf),
    ));
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const rejectSelected = () => {
    // "Reject" in this UI removes rows from the scan result entirely;
    // they aren't auto-applied later. Server is untouched.
    setScanResult((prev) => prev && {
      ...prev,
      items: prev.items.filter((it) => !selectedKeys.has(keyOf(it))),
    });
    clearSelection();
    toast({ title: `${selectedKeys.size} row(s) rejected` });
  };

  const exportJson = () => {
    const blob = new Blob(
      [JSON.stringify({
        scan_id: scanResult?.scan_id,
        timestamp: new Date().toISOString(),
        metrics: scanResult?.metrics,
        items: scanResult?.items || [],
      }, null, 2)],
      { type: 'application/json' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `text-repair-scan-${(scanResult?.scan_id || 'preview').slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openApply = () => {
    if (selectedItems.length === 0) {
      toast({ title: 'No rows selected', variant: 'destructive' });
      return;
    }
    setAcknowledgeLowConf(false);
    setApplyNote('');
    setShowConfirm(true);
  };

  const doApply = async () => {
    if (selectedLowConfCount > 0 && !acknowledgeLowConf) {
      toast({
        title: 'Low-confidence acknowledgement required',
        description: `${selectedLowConfCount} row(s) below ${safeFloor.toFixed(2)} — tick the box to proceed.`,
        variant: 'destructive',
      });
      return;
    }
    setApplying(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/text-repair/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          repairs: selectedItems,
          acknowledge_low_confidence: acknowledgeLowConf,
          note: applyNote || null,
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status} — ${txt}`);
      }
      const data = await res.json();
      toast({
        title: `Batch ${data.batch_id.slice(0, 8)} committed`,
        description: `Applied ${data.applied}, skipped ${data.skipped}.`,
      });
      // Remove applied rows from the scan view; keep skipped rows so the
      // operator can see what was rejected (stale-original, etc.).
      const appliedKeys = new Set(
        data.items
          .filter((r) => r.status === 'applied')
          .map((r) => `${r.collection}|${r.doc_id}|${r.element}|${r.field}`),
      );
      setScanResult((prev) => prev && {
        ...prev,
        items: prev.items.filter((it) => !appliedKeys.has(keyOf(it))),
      });
      clearSelection();
      setShowConfirm(false);
    } catch (e) {
      toast({ title: 'Apply failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setApplying(false);
    }
  };

  const doRollback = async (batchId) => {
    if (!window.confirm(`Roll back batch ${batchId.slice(0, 8)}? This restores the BEFORE values on every audit record.`)) return;
    setRollingBack(batchId);
    try {
      const res = await fetch(`${API_URL}/api/admin/text-repair/rollback/${batchId}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${await res.text()}`);
      const data = await res.json();
      toast({
        title: 'Rollback complete',
        description: `Restored ${data.restored}, skipped ${data.skipped}.`,
      });
      await fetchBatches();
    } catch (e) {
      toast({ title: 'Rollback failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setRollingBack(null);
    }
  };

  // ── render ────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Wand2 className="w-6 h-6 text-violet-600" />
              Text Repair
            </h1>
            <p className="text-sm text-gray-500">
              Detect and fix design text whose word-boundary spaces were lost upstream of the editor (e.g. <code>4BATHROOMS → 4 BATHROOMS</code>).
            </p>
          </div>
        </header>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="scan">Scan & Review</TabsTrigger>
            <TabsTrigger value="batches">Batches & Rollback</TabsTrigger>
          </TabsList>

          {/* ── Scan tab ──────────────────────────────────────────── */}
          <TabsContent value="scan" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  Scan
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <Button onClick={runScan} disabled={scanning}>
                  {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  {scanning ? 'Scanning…' : 'Run scan'}
                </Button>
                {scanResult && (
                  <Button variant="outline" onClick={exportJson}>
                    <Download className="w-4 h-4 mr-2" /> Export JSON report
                  </Button>
                )}
                {scanResult && (
                  <div className="ml-auto flex flex-wrap items-center gap-2 text-sm text-gray-600">
                    <Badge variant="secondary">{scanResult.metrics?.total_candidates ?? 0} candidate(s)</Badge>
                    <Badge variant="secondary">avg conf {(scanResult.metrics?.avg_confidence ?? 0).toFixed(2)}</Badge>
                    {scanResult.metrics?.below_safe_threshold > 0 && (
                      <Badge variant="destructive">{scanResult.metrics.below_safe_threshold} below floor</Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {scanResult && scanResult.metrics?.top_originals?.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Top recurring originals</CardTitle></CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {scanResult.metrics.top_originals.map((t) => (
                    <Badge key={t.original} variant="outline">
                      ×{t.count} <span className="ml-1 font-mono">{t.original}</span>
                    </Badge>
                  ))}
                </CardContent>
              </Card>
            )}

            {scanResult && (
              <Card>
                <CardContent className="pt-4 space-y-4">
                  {/* Filters */}
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[240px]">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search original, proposed, doc ID, element…"
                        className="pl-9"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-gray-400" />
                      <select
                        className="border rounded px-2 py-1 text-sm bg-white"
                        value={filterRule}
                        onChange={(e) => setFilterRule(e.target.value)}
                      >
                        <option value="all">All rules</option>
                        <option value="dictionary">dictionary (1.00)</option>
                        <option value="digit-letter">digit-letter (0.92)</option>
                        <option value="dictionary-tile">dictionary-tile (0.80+)</option>
                        <option value="mixed">mixed</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={filterLowConf} onCheckedChange={setFilterLowConf} />
                      Show only &lt; {safeFloor.toFixed(2)}
                    </label>
                  </div>

                  {/* Selection controls */}
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span>{selectedKeys.size} of {filtered.length} selected</span>
                    <Button variant="outline" size="sm" onClick={selectAllHighConf}>
                      Select all ≥ {safeFloor.toFixed(2)}
                    </Button>
                    <Button variant="outline" size="sm" onClick={clearSelection} disabled={selectedKeys.size === 0}>
                      Clear
                    </Button>
                    <Button variant="outline" size="sm" onClick={rejectSelected} disabled={selectedKeys.size === 0}>
                      <X className="w-4 h-4 mr-1" /> Reject selected
                    </Button>
                    <Button onClick={openApply} disabled={selectedKeys.size === 0 || applying}
                            className="ml-auto bg-violet-600 hover:bg-violet-700">
                      Approve & apply ({selectedKeys.size})
                    </Button>
                  </div>

                  {/* Table */}
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox
                              checked={allOnPageSelected}
                              data-state={!allOnPageSelected && someOnPageSelected ? 'indeterminate' : undefined}
                              onCheckedChange={togglePage}
                            />
                          </TableHead>
                          <TableHead>Collection</TableHead>
                          <TableHead>Document ID</TableHead>
                          <TableHead>Element ID</TableHead>
                          <TableHead>Field</TableHead>
                          <TableHead>Original</TableHead>
                          <TableHead>Suggested repair</TableHead>
                          <TableHead className="w-24">Conf</TableHead>
                          <TableHead>Rule</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pageItems.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center text-sm text-gray-500 py-8">
                              No candidates match the current filters.
                            </TableCell>
                          </TableRow>
                        )}
                        {pageItems.map((it) => {
                          const k = keyOf(it);
                          const low = it.confidence < safeFloor;
                          return (
                            <TableRow key={k}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedKeys.has(k)}
                                  onCheckedChange={() => toggleRow(it)}
                                />
                              </TableCell>
                              <TableCell className="font-mono text-xs">{it.collection}</TableCell>
                              <TableCell className="font-mono text-xs">{it.doc_id}</TableCell>
                              <TableCell className="font-mono text-xs">{it.element}</TableCell>
                              <TableCell className="font-mono text-xs">{it.field}</TableCell>
                              <TableCell className="font-mono text-sm">{JSON.stringify(it.original)}</TableCell>
                              <TableCell className="font-mono text-sm text-emerald-700">{JSON.stringify(it.proposed)}</TableCell>
                              <TableCell>
                                <Badge variant={low ? 'destructive' : 'secondary'}>
                                  {it.confidence.toFixed(2)}
                                </Badge>
                              </TableCell>
                              <TableCell><Badge variant="outline">{it.rule}</Badge></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {pageCount > 1 && (
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm"
                              onClick={() => setPage((p) => Math.max(0, p - 1))}
                              disabled={pageSafe === 0}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-gray-600">
                        Page {pageSafe + 1} / {pageCount}
                      </span>
                      <Button variant="outline" size="sm"
                              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                              disabled={pageSafe >= pageCount - 1}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Batches tab ───────────────────────────────────────── */}
          <TabsContent value="batches" className="space-y-4">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Prior repair batches</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchBatches} disabled={batchesLoading}>
                  {batchesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Batch ID</TableHead>
                      <TableHead>Applied at</TableHead>
                      <TableHead>By</TableHead>
                      <TableHead>Repairs</TableHead>
                      <TableHead>Rolled back</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="w-32 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.length === 0 && !batchesLoading && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-sm text-gray-500 py-8">
                          No batches yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {batches.map((b) => {
                      const fully = b.rolled_back >= b.count;
                      return (
                        <TableRow key={b.batch_id}>
                          <TableCell className="font-mono text-xs">{b.batch_id.slice(0, 8)}…</TableCell>
                          <TableCell className="text-xs">{b.applied_at ? new Date(b.applied_at).toLocaleString() : '—'}</TableCell>
                          <TableCell className="text-xs">{b.applied_by?.email || '—'}</TableCell>
                          <TableCell><Badge variant="secondary">{b.count}</Badge></TableCell>
                          <TableCell>
                            {b.rolled_back > 0 && <Badge variant={fully ? 'outline' : 'destructive'}>{b.rolled_back}</Badge>}
                          </TableCell>
                          <TableCell className="text-xs text-gray-600">{b.note || ''}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm"
                                    onClick={() => doRollback(b.batch_id)}
                                    disabled={fully || rollingBack === b.batch_id}>
                              {rollingBack === b.batch_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><Undo2 className="w-4 h-4 mr-1" /> Roll back</>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Apply confirmation dialog ───────────────────────────── */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              Confirm apply
            </DialogTitle>
            <DialogDescription>
              Applies {selectedItems.length} repair(s) atomically under one batch ID.
              Every change is written to <code>text_repair_audit</code> and is
              reversible from the Batches tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span>Total selected</span><span className="font-semibold">{selectedItems.length}</span></div>
            <div className="flex justify-between">
              <span>Below confidence floor ({safeFloor.toFixed(2)})</span>
              <span className={selectedLowConfCount > 0 ? 'font-semibold text-amber-600' : 'font-semibold'}>{selectedLowConfCount}</span>
            </div>
            {selectedLowConfCount > 0 && (
              <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                <Checkbox checked={acknowledgeLowConf} onCheckedChange={setAcknowledgeLowConf} className="mt-0.5" />
                <span>I acknowledge {selectedLowConfCount} row(s) are below the safe-confidence floor. Apply them anyway.</span>
              </label>
            )}
            <div className="space-y-1">
              <Label htmlFor="apply-note" className="text-xs">Note (optional)</Label>
              <Input id="apply-note" value={applyNote} onChange={(e) => setApplyNote(e.target.value)}
                     placeholder="e.g. realty template cleanup 2026-05-28" maxLength={500} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={applying}>Cancel</Button>
            <Button onClick={doApply} disabled={applying} className="bg-violet-600 hover:bg-violet-700">
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {applying ? 'Applying…' : 'Apply now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminTextRepair;
